import React from 'react'

import {
  MemberStatus,
  MemberType,
  PatchGroupMemberBody,
  SelectGroupMember
} from 'types/GlobalGroup'

import { useGroup, useIsOwner } from 'redux/hooks/Groups'
import { useGroupMemberActions } from 'redux/actions/GroupMembers'
import { useParams } from 'utils/hooks'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Chip from 'components_new/atoms/Chip'

import ContentBox from 'components_new/molecules/ContentBox'
import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import GenericListItem from 'components_new/molecules/GenericListItem'

import GroupAddMemberButton from 'components_new/organisms/GroupAddMemberButton'

import GroupTemplate from 'components_new/templates/GroupTemplate'

const GroupMembers = () => {
  const { tryDeleteGroupMember, tryUpdateGroupMember } = useGroupMemberActions()
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()

  const group = useGroup(id)
  const isOwner = useIsOwner(id)

  if (!group) {
    return null
  }

  const handleToggleUserGroupRole = (user: SelectGroupMember) => {
    let body: PatchGroupMemberBody = {}

    if (user.status === MemberStatus.PENDING) {
      body = {
        status: MemberStatus.APPROVED
      }
    } else if (user.type === MemberType.MEMBER) {
      body = {
        type: MemberType.OWNER
      }
    } else {
      // Patching owner to member.
      body = {
        type: MemberType.MEMBER
      }
    }

    tryUpdateGroupMember(user.id, body, group.id)
  }

  return (
    <GroupTemplate
      subpage={{
        icon: { name: 'Users' },
        title: t('global', 'members')
      }}
      topbar={
        isOwner ? { actions: <GroupAddMemberButton id={id} /> } : undefined
      }
    >
      <Box>
        {/*-- pending --*/}
        {isOwner && group.pending.length > 0 ? (
          <ContentBox
            color="success"
            disablePadding={true}
            externalHeader={{
              title: t('groups', 'requests'),
              titleAdornment: <Chip label={group.pending.length} size="small" />
            }}
            sx={{ mb: 4 }}
          >
            {group.pending.map((member) => (
              <GenericListItem
                actions={
                  <>
                    <Button
                      onClick={() => tryDeleteGroupMember(member.id, group.id)}
                      size="small"
                      variant="text"
                    >
                      {t('global', 'deny')}
                    </Button>
                    <Button
                      onClick={() =>
                        tryUpdateGroupMember(
                          member.id,
                          { status: MemberStatus.APPROVED },
                          group.id
                        )
                      }
                      size="small"
                    >
                      {t('global', 'accept')}
                    </Button>
                  </>
                }
                hasHover={true}
                key={member.id}
                title={member.name}
                variant="user"
              />
            ))}
          </ContentBox>
        ) : null}

        {/*-- owners --*/}
        <ContentBox
          color="default"
          disablePadding={true}
          externalHeader={{
            title: t('global', 'owners'),
            titleAdornment: <Chip label={group.owners.length} size="small" />
          }}
          sx={{ mb: 4 }}
        >
          {group.owners.length > 0 ? (
            group.owners.map((owner) => (
              <GenericListItem
                hasHover={isOwner}
                key={owner.id}
                options={
                  isOwner
                    ? [
                        {
                          divider: true,
                          disabled: group.owners.length === 1,
                          iconName: 'SupervisorAccountOutlined',
                          infoTip:
                            group.owners.length === 1
                              ? t('groups', 'no_owner_infotip')
                              : undefined,
                          onClick: () => handleToggleUserGroupRole(owner),
                          title: t('groups', 'make_member')
                        },
                        {
                          color: 'error',
                          disabled: group.owners.length === 1,
                          iconName: 'LogoutOutlined',
                          infoTip:
                            group.owners.length === 1
                              ? t('groups', 'no_owner_infotip')
                              : undefined,
                          onClick: () =>
                            tryDeleteGroupMember(owner.id, group.id),
                          title: t('groups', 'remove_from_group')
                        }
                      ]
                    : undefined
                }
                title={owner.name}
                variant="user"
              />
            ))
          ) : (
            <EmptyPlaceholder
              background={true}
              size="xsmall"
              title={t('global', 'no_owner')}
            />
          )}
        </ContentBox>

        {/*-- members --*/}
        <ContentBox
          color="default"
          disablePadding={true}
          externalHeader={{
            title: t('groups', 'members'),
            titleAdornment: <Chip label={group.members.length} size="small" />
          }}
        >
          {group.members.length > 0 ? (
            group.members.map((member) => (
              <GenericListItem
                hasHover={isOwner}
                key={member.id}
                options={
                  isOwner
                    ? [
                        {
                          divider: true,
                          iconName: 'SupervisorAccountOutlined',
                          infoTip: '',
                          onClick: () => handleToggleUserGroupRole(member),
                          title: t('groups', 'make_owner')
                        },
                        {
                          color: 'error',
                          iconName: 'LogoutOutlined',
                          infoTip: '',
                          onClick: () =>
                            tryDeleteGroupMember(member.id, group.id),
                          title: t('groups', 'remove_from_group')
                        }
                      ]
                    : undefined
                }
                title={member.name}
                variant="user"
              />
            ))
          ) : (
            <EmptyPlaceholder
              background={true}
              size="xsmall"
              title={t('groups', 'no_members')}
            />
          )}
        </ContentBox>
      </Box>
    </GroupTemplate>
  )
}

export default GroupMembers
